import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Err } from '../ui.js';
import { runOnboarding, ApiError } from '../api.js';
import type { OnboardResult } from '../api.js';

// ─────────────────────────────────────────────────────────────────────────────
// THE FIRST SCREEN OF A FRESH INSTALL.
//
// Everything in this app was built against a workspace that already had 28
// accounts, an ICP and five personas. Pointed at an empty database it became a
// set of screens that each said "nothing yet" without ever saying what to do —
// which is precisely the state a new operator gets, and the reason a tool that
// works for its author fails for everyone else.
//
// ONE QUESTION, NOT THREE FORMS. What you sell, who you sell it to, and who
// inside decides are three screens of fields and one paragraph of speech.
// People can produce the paragraph; almost nobody will fill the fields in cold.
// Everything derived here is editable afterwards — this is a starting point,
// not an interview.
// ─────────────────────────────────────────────────────────────────────────────

const EXAMPLE =
  "I'm a fractional CMO. I work with construction software companies doing "
  + '$5M to $150M that sell through a sales team and whose marketing has stalled '
  + '— usually the founder is still running it, or there is one overworked '
  + 'demand-gen person with a director title. I sell a fractional CMO retainer, '
  + 'hourly consulting, and board-level advisory.';

export function Welcome({ onDone }: { onDone: () => void }) {
  const navigate = useNavigate();
  const [text, setText] = useState('');
  const [name, setName] = useState('');
  const [website, setWebsite] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<OnboardResult | null>(null);
  // A 503 from the drafter means "no key", which is a different problem from a
  // bad answer and needs a different way out.
  const [needsKey, setNeedsKey] = useState(false);

  async function go() {
    setBusy(true); setError(null);
    try {
      setResult(await runOnboarding({
        text: text.trim(),
        org_name: name.trim() || undefined,
        website: website.trim() || undefined,
      }));
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
      setNeedsKey(e instanceof ApiError && e.status === 503);
    } finally { setBusy(false); }
  }

  if (result) {
    return (
      <div className="welcome">
        <div className="welcomecard">
          <h1>Here's what I set up</h1>
          <p className="lede">{result.positioning}</p>

          {/* Say which path ran. The parsed one is materially rougher and an
              operator who thinks Claude wrote it will not check it. */}
          {result.source === 'parsed' && (
            <p className="warnbox">
              <strong>Read without Claude.</strong> No Anthropic key in this
              workspace, so I pattern-matched your description instead of
              understanding it. Everything below is a starting point that needs
              your eye more than usual.
            </p>
          )}

          <dl className="facts">
            <dt>Selling</dt><dd>{result.offerings} offering{result.offerings === 1 ? '' : 's'}</dd>
            <dt>To</dt><dd>{result.icp_name}</dd>
            <dt>Through</dt><dd>{result.personas} persona{result.personas === 1 ? '' : 's'}</dd>
          </dl>

          {/* A draft that hides its own holes is worse than a short one. */}
          {result.gaps && (
            <p className="warnbox">
              <strong>Left blank, because you didn't say:</strong> {result.gaps}
            </p>
          )}

          <p className="meta">{result.note}</p>

          <div className="actions">
            <button className="primary" onClick={onDone}>Go to my workspace</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="welcome">
      <div className="welcomecard">
        <span className="welcomelogo"><img src="/logo.png" alt="" /></span>
        <h1>What do you sell, and who buys it?</h1>
        <p className="lede">
          A paragraph is enough. From it I'll draft what you sell, the profile of a
          company worth your time, and the people inside one who decide — then you
          correct all three. Everything after this reads from it: research, contact
          search, scoring, and every email the app drafts.
        </p>

        <Err>{error}</Err>

        {/* THE ONE DEAD END THIS SCREEN COULD CREATE.
            Welcome replaces the whole app — there is no nav behind it — and
            drafting needs an Anthropic key that a fresh install is guaranteed
            not to have. Without this the operator gets "add a key in Settings"
            and no way to reach Settings. The house rule is that nothing tells
            you to paste a token somewhere that does not exist; a route that
            exists but cannot be reached fails it the same way. */}
        {needsKey && (
          <p className="warnbox">
            <strong>No Anthropic key yet.</strong> Add one and come back — this
            screen reappears until the workspace has something in it.{' '}
            <button className="linkish"
              onClick={() => { onDone(); navigate('/settings'); }}>
              Open Settings →
            </button>
          </p>
        )}

        <textarea
          rows={7}
          value={text}
          autoFocus
          placeholder={EXAMPLE}
          onChange={(e) => setText(e.target.value)}
        />
        <button className="linkish" onClick={() => setText(EXAMPLE)}>
          Use the example
        </button>

        <div className="pair">
          <label className="field">
            <span className="field-label">Your company</span>
            <input value={name} placeholder="Acme Advisory"
              onChange={(e) => setName(e.target.value)} />
          </label>
          <label className="field">
            <span className="field-label">Website</span>
            <input value={website} placeholder="acmeadvisory.com"
              onChange={(e) => setWebsite(e.target.value)} />
          </label>
        </div>

        <div className="actions">
          <button className="primary" disabled={busy || text.trim().length < 30}
            onClick={() => void go()}>
            {busy ? 'Setting up…' : 'Set up my workspace'}
          </button>
          <button className="ghost" onClick={onDone}>Skip — I'll do it by hand</button>
        </div>

        <p className="fieldhint">
          Works without an API key — add an Anthropic key in Settings later and
          the drafts get much better. Nothing here is permanent; every field it
          writes is editable.
        </p>
      </div>
    </div>
  );
}
